import * as React from 'react';
import { useMemo } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    CreditCardIcon,
    FruitfulCustomIcon,
    FullScreenSpinner,
    H5Emphasized,
    PiggyBankIcon,
    SelectTile,
    Sheet,
    View,
    YStack,
} from 'smoothie';

import { formatInterestRateOrYieldPercent } from '../../../../../common/utils/bankingUtils';
import useGetCurrentInterestRateAndApy from '../../../../hooks/useGetCurrentInterestRateAndApy';
import type InterestRate from '../../../../types/InterestRate';
import { DummyCategoryCard } from '../DummyCategoryCard';

export enum SpendType {
    SPEND = 'spend',
    SAVE = 'save',
}

const Spend = ({
    jointAccount,
    icon,
    color,
    nickname,
    fromKyc,
    onSelect,
}: {
    jointAccount: boolean;
    icon?: string | null;
    color?: string | null;
    nickname?: string | null;
    fromKyc: boolean;
    onSelect: (spendType: SpendType) => void;
}) => {
    return (
        <YStack
            justifyContent="space-between"
            gap={24}
            $sm={{
                gap: 80,
                width: 700,
                alignSelf: 'center',
                height: 700,
                justifyContent: 'flex-start',
            }}
        >
            <YStack alignSelf="center" alignItems="center">
                <FruitfulCustomIcon
                    icon={icon ?? 'bankNote'}
                    color={color ?? 'green'}
                    size="xl"
                    showShadow
                />
                <H5Emphasized marginTop={24}>
                    {!fromKyc
                        ? nickname
                        : jointAccount
                          ? 'Our Cash'
                          : 'My Cash'}
                </H5Emphasized>
            </YStack>
            <YStack marginBottom={24}>
                <H5Emphasized
                    textAlign="center"
                    marginHorizontal={32}
                    marginBottom={32}
                >
                    Do you plan to spend regularly from this account?
                </H5Emphasized>
                <YStack gap={16}>
                    <SelectTile
                        title="Yes, I plan to spend and make regular purchases from here"
                        icon={CreditCardIcon}
                        onPress={() => {
                            onSelect(SpendType.SPEND);
                        }}
                    />
                    <SelectTile
                        title="No, I just plan to use this for saving right now"
                        icon={PiggyBankIcon}
                        onPress={() => {
                            onSelect(SpendType.SAVE);
                        }}
                    />
                </YStack>
            </YStack>
        </YStack>
    );
};

interface InfoScreenProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SpendInfoSheet = ({ isOpen, onClose }: InfoScreenProps) => {
    const { data: currentInterestRate, isLoading: isLoadingInterestRate } =
        useGetCurrentInterestRateAndApy({});

    const formattedAPY = useMemo(
        () =>
            formatInterestRateOrYieldPercent(
                (currentInterestRate as InterestRate)?.apy ?? 0,
            ),
        [currentInterestRate],
    );

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Clarify your cash"
            showCloseButton
        >
            <YStack marginTop={16} maxWidth={500}>
                {isLoadingInterestRate ? (
                    <FullScreenSpinner />
                ) : (
                    <>
                        <View
                            marginHorizontal={70}
                            marginTop={32}
                            alignItems="center"
                            justifyContent="center"
                            flexDirection="row"
                        >
                            <DummyCategoryCard
                                icon={CreditCardIcon}
                                label="Spend"
                                amount="$4,580"
                                rotate="-12deg"
                                alignSelf="flex-start"
                                accountCount={2}
                                borderRadius={8}
                                zIndex={1}
                            />
                            <DummyCategoryCard
                                icon={PiggyBankIcon}
                                label="Save"
                                amount="$13,261"
                                accountCount={3}
                                borderRadius={8}
                                marginTop={20}
                                alignSelf="flex-end"
                                rotate="12deg"
                                zIndex={2}
                            />
                        </View>
                        <BodyLargeEmphasized marginTop={36}>
                            Why are you asking?
                        </BodyLargeEmphasized>
                        <BodyMediumRegular color="$gray1000" marginTop={8}>
                            This helps us categorize your account in the
                            appropriate “Spend” or “Save” sections, making it
                            easy for you to keep your cash organized and
                            intentional. Don’t worry, you can re-categorize
                            accounts at any time.
                        </BodyMediumRegular>
                        <BodyLargeEmphasized marginTop={16}>
                            Does this choice affect the APY I earn?
                        </BodyLargeEmphasized>
                        <BodyMediumRegular color="$gray1000" marginTop={8}>
                            No, all of our cash accounts earn {formattedAPY} APY
                            and allow unlimited transactions. Your choice only
                            affects how this account is categorized and
                            displayed, which you can change at anytime.
                        </BodyMediumRegular>
                    </>
                )}
            </YStack>
        </Sheet>
    );
};

export default Spend;
